/**
 * @file keep-alive 搜索栏断连修复 Hook
 * @description
 * react-activation 缓存页在“被切走 → 期间布局变化（如收起菜单）→ 再切回”时，
 * 会重新挂载页面，ProTable 的搜索表单实例与 <Form> 断连
 * （控制台可见 antd 警告：Instance created by 'useForm' is not connected to any Form element），
 * 表现为搜索栏筛选项全部消失、只剩“重置/查询”。
 *
 * 修复思路（不重挂整页、不丢搜索值、且不破坏“重置”）：
 * 1. 离开本页（useUnactivate，此刻表单仍正常）用 formRef 快照当前搜索值；
 * 2. 切回本页（useActivate）稍等布局稳定后检测搜索栏是否断连（有 query-filter 但无筛选项）；
 * 3. 断连则给 ProTable 换 key 重挂（仅重建表格与其表单，页面组件 fiber 保活、快照 ref 不丢），
 *    重挂后用 setFieldsValue **命令式**填回上次搜索值并触发查询。
 *
 * 注意：快照**不作为** form.initialValues。因为 ProTable 的“重置”会把表单还原到
 * initialValues，若把快照塞进 initialValues，重置会变成“恢复上次搜索”而非清空。
 * 因此本 hook 返回的 initialValues 恒为 undefined（各页面照常解构使用，重置基线保持为空/页面默认）。
 *
 * 用法：
 *   const { rootRef, remountKey, formRef, initialValues } = useKeepAliveSearchFix();
 *   return (
 *     <div ref={rootRef}>
 *       <ProTable key={remountKey} formRef={formRef} form={{ initialValues }} ... />
 *     </div>
 *   );
 */
import type { ProFormInstance } from "@ant-design/pro-components";
import { useRef, useState } from "react";
import { useActivate, useUnactivate } from "react-activation";

export function useKeepAliveSearchFix() {
  const rootRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<ProFormInstance | undefined>(undefined);
  const lastValuesRef = useRef<Record<string, any>>({});
  const [remountKey, setRemountKey] = useState(0);

  useUnactivate(() => {
    const values = formRef.current?.getFieldsValue?.();
    if (values) lastValuesRef.current = values;
  });

  useActivate(() => {
    setTimeout(() => {
      const root = rootRef.current;
      const qf = root?.querySelector(".ant-pro-query-filter");
      if (!qf) return; // 本页没有搜索栏
      const realItems = Array.from(
        qf.querySelectorAll(".ant-form-item")
      ).filter((fi) => !fi.className.includes("ant-pro-query-filter-actions"));
      if (realItems.length !== 0) return; // 未断连，无需处理
      // 断连：重挂 ProTable
      setRemountKey((k) => k + 1);

      // 重挂后把上次搜索值命令式填回并触发查询（不经由 initialValues，避免污染“重置”基线）
      const snapshot = lastValuesRef.current;
      if (!snapshot || Object.keys(snapshot).length === 0) return;
      let tries = 0;
      const restore = () => {
        const f = formRef.current;
        if (f?.setFieldsValue) {
          f.setFieldsValue(snapshot);
          f.submit?.();
        } else if (tries++ < 12) {
          setTimeout(restore, 50);
        }
      };
      // 等新 ProTable 挂载、formRef 指向新表单后再填
      setTimeout(restore, 80);
    }, 150);
  });

  return {
    /** 挂到包裹 ProTable 的容器上，用于精确定位本页搜索栏 */
    rootRef,
    /** 传给 ProTable 的 key，断连时自动变化以触发重挂 */
    remountKey,
    /** 传给 ProTable 的 formRef，用于快照/恢复搜索值 */
    formRef,
    /**
     * 传给 ProTable 的 form.initialValues。恒为 undefined —— 快照不作为重置基线，
     * 否则会破坏“重置清空”。重挂后的恢复改用 setFieldsValue 命令式完成。
     */
    initialValues: undefined as Record<string, any> | undefined,
  };
}

export default useKeepAliveSearchFix;
